import { useNavigate, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { SummaryCard } from '@nucleus-commerce/admin-kit/forms';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import type { Product } from '@nucleus-commerce/api-client/admin';

import {
  ProductEngagementSection,
  ProductStoresSection,
  PublishToStoresModal,
} from '@/features/store-catalog';

import { fromProduct, newProductForm, toProductInput } from '../api/mappers';
import { ConfiguratorSection } from '../components/configurator-section';
import { ProductCollectionsSection } from '../components/product-collections-section';
import { ProductForm } from '../components/product-form';
import { ProductMediaSection } from '../components/product-media-section';
import { ProductPageHeader } from '../components/product-page-header';
import { RelationsSection } from '../components/relations-section';
import { VariantsSection } from '../components/variants-section';
import { useCreateProduct } from '../hooks/use-create-product';
import { useProduct } from '../hooks/use-product';
import { useProductGalleryDraft } from '../hooks/use-product-gallery-draft';
import { useProductMedia } from '../hooks/use-product-media';
import { useSaveProduct } from '../hooks/use-save-product';
import { useVariants } from '../hooks/use-variants';

const EDIT_ROUTE = '/_authed/_active/org/products/$productId' as const;

function ProductCreate() {
  const { t } = useTranslation('products');
  const navigate = useNavigate();
  const create = useCreateProduct();

  return (
    <PageContent>
      <PageHeader
        title={t('add')}
        backTo={{ to: '/org/products' }}
        backLabel={t('masterPage.back')}
      />
      <ProductForm
        mode="create"
        defaultValues={newProductForm()}
        isSubmitting={create.isPending}
        onSubmit={async (values, attributes) => {
          const product = await create.mutateAsync(toProductInput(values, attributes));
          await navigate({
            to: '/org/products/$productId',
            params: { productId: product.id },
            replace: true,
          });
        }}
      >
        <SummaryCard
          title={t('afterSave.title')}
          className="bg-muted/40"
          tour="catalog.product-new.after-save"
        >
          <p className="text-muted-foreground text-[13px]">{t('afterSave.description')}</p>
        </SummaryCard>
      </ProductForm>
    </PageContent>
  );
}

function ProductEdit({ productId }: { productId: string }) {
  // Data is prefetched by the route loader, so this resolves from cache.
  const { data: product } = useProduct(productId);
  const variants = useVariants(productId);
  const media = useProductMedia(productId);
  // The form takes the saved values in place, without remounting: the rail's collection drafts and
  // the gallery draft are saved next to it, and must survive if theirs is the save that fails.
  const gallery = useProductGalleryDraft(productId, product?.featuredMediaId);
  const save = useSaveProduct(productId);
  const [publishing, setPublishing] = useState(false);

  if (!product) return null;
  const featuredId = product.featuredMediaId ?? media.data?.[0]?.mediaId ?? null;
  const featuredAlt = media.data?.find((row) => row.mediaId === featuredId)?.media.alt ?? null;
  const openPublish = () => setPublishing(true);

  return (
    <PageContent>
      <ProductForm
        mode="edit"
        defaultValues={fromProduct(product)}
        reseed={(saved) => fromProduct(saved as Product)}
        isSubmitting={save.isPending}
        variants={variants.data}
        featuredImage={featuredId ? { alt: featuredAlt } : null}
        saved={product}
        onSubmit={save.onSubmit}
        header={<ProductPageHeader product={product} />}
        media={<ProductMediaSection gallery={gallery} />}
        collections={<ProductCollectionsSection productId={product.id} onPublish={openPublish} />}
        aside={
          <>
            <ProductStoresSection product={product} onPublish={openPublish} />
            <Can permission="reports.read">
              <ProductEngagementSection productId={product.id} />
            </Can>
          </>
        }
      >
        <VariantsSection product={product} />
        <Can permission="configurator.read">
          <div data-tour="catalog.product-edit.configurator">
            <ConfiguratorSection product={product} />
          </div>
        </Can>
        <div data-tour="catalog.product-edit.relations">
          <RelationsSection product={product} />
        </div>
      </ProductForm>
      <PublishToStoresModal product={product} open={publishing} onOpenChange={setPublishing} />
    </PageContent>
  );
}

export function ProductCreatePage() {
  return (
    <FeatureErrorBoundary name="products.create">
      <ProductCreate />
    </FeatureErrorBoundary>
  );
}

export function ProductEditPage() {
  const { productId } = useParams({ from: EDIT_ROUTE });
  return (
    <FeatureErrorBoundary name="products.edit">
      {/* Previous and next land on this same route: every draft on the page starts over. */}
      <ProductEdit key={productId} productId={productId} />
    </FeatureErrorBoundary>
  );
}
